import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
} from '@mui/material';
import { CheckCircle, Edit } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  content: string;
  recipients: string[];
  status: 'draft' | 'scheduled' | 'sent';
  sent_at?: string;
  open_rate?: number;
  click_rate?: number;
}

interface CampaignsTabProps {
  campaigns: EmailCampaign[];
}

const CampaignsTab: React.FC<CampaignsTabProps> = ({ campaigns }) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
      >
        {t('admin.emailCampaigns')}
      </Typography>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: { xs: 500, sm: 650 } }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.campaignName')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{t('admin.subject')}</TableCell>
              <TableCell>{t('admin.recipients')}</TableCell>
              <TableCell>{t('common.status')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('admin.performance')}</TableCell>
              <TableCell align="center">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map((campaign) => (
              <TableRow key={campaign.id}>
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    {campaign.name}
                  </Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{campaign.subject}</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{campaign.recipients.length}</TableCell>
                <TableCell>
                  <Chip
                    label={campaign.status}
                    size="small"
                    color={campaign.status === 'sent' ? 'success' : 'default'}
                    icon={campaign.status === 'sent' ? <CheckCircle /> : undefined}
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                  />
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {campaign.status === 'sent' && (
                    <Box>
                      <Typography variant="caption" display="block">
                        Open Rate: {campaign.open_rate}%
                      </Typography>
                      <Typography variant="caption" display="block">
                        Click Rate: {campaign.click_rate}%
                      </Typography>
                    </Box>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit />}
                    sx={{
                      borderColor: 'rgba(0,212,255,0.5)',
                      color: '#00d4ff',
                      '&:hover': {
                        borderColor: '#00d4ff',
                        bgcolor: 'rgba(0,212,255,0.1)',
                      },
                    }}
                  >
                    {t('common.edit')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CampaignsTab;